/**
 * Barcode Scanner Hook
 * Handles barcode input from USB scanner or manual typing.
 * Also supports product name search with dropdown results.
 * Plays a beep sound on successful scan using Web Audio API.
 */
import { useCallback, useRef, useState, useEffect } from 'react';
import { apiFetch } from '@/lib/api';

export interface SearchProduct {
  id: string;
  name: string;
  barcode: string;
  sale_price: number;
  unit: string;
  stock_quantity: number;
}

interface UseBarcodeScanOptions {
  onScan: (barcode: string) => void;
  onInvalidCode?: (code: string) => void;
  onProductSelect?: (product: SearchProduct) => void;
}

// Barcode validation: must be numeric and between 4-13 digits
function isValidBarcode(code: string): boolean {
  return /^\d{4,13}$/.test(code);
}

// Check if input looks like a barcode (all digits)
function looksLikeBarcode(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

// Play a short beep using Web Audio API
function playBeep(frequency = 1000, duration = 100) {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;

    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, duration);
  } catch {
    // Web Audio API not available, silently fail
  }
}

// Play error sound
function playErrorBeep() {
  playBeep(300, 200);
}

export function useBarcodeScan({ onScan, onInvalidCode, onProductSelect }: UseBarcodeScanOptions) {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastScanTime = useRef<number>(0);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [searchResults, setSearchResults] = useState<SearchProduct[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  const searchProducts = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await apiFetch(`/api/products?search=${encodeURIComponent(query)}&limit=8`);
      if (response.ok) {
        const data = await response.json();
        const products = Array.isArray(data) ? data : data.products || data.data || [];
        setSearchResults(products);
        setShowDropdown(products.length > 0);
        setSelectedIndex(-1);
      }
    } catch {
      // Silently fail on search errors
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSubmit = useCallback(
    (value: string) => {
      const code = value.trim();
      if (!code) return;

      // Debounce: prevent double-scan within 500ms
      const now = Date.now();
      if (now - lastScanTime.current < 500) return;
      lastScanTime.current = now;

      if (isValidBarcode(code)) {
        playBeep();
        onScan(code);
      } else if (looksLikeBarcode(code)) {
        // All digits but invalid length
        playErrorBeep();
        onInvalidCode?.(code);
      } else {
        // Text input - if dropdown is showing and item selected, use that
        if (showDropdown && selectedIndex >= 0 && searchResults[selectedIndex]) {
          selectProduct(searchResults[selectedIndex]);
          return;
        }
        // Otherwise treat as barcode attempt
        playErrorBeep();
        onInvalidCode?.(code);
      }
    },
    [onScan, onInvalidCode, showDropdown, selectedIndex, searchResults],
  );

  const selectProduct = useCallback(
    (product: SearchProduct) => {
      playBeep();
      onProductSelect?.(product);
      setShowDropdown(false);
      setSearchResults([]);
      setSelectedIndex(-1);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onProductSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // If it looks like a barcode (all numbers), don't search
      if (looksLikeBarcode(value) || value.trim().length < 2) {
        setShowDropdown(false);
        setSearchResults([]);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        return;
      }

      // Debounce search for text input
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        searchProducts(value.trim());
      }, 300);
    },
    [searchProducts],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle dropdown navigation
      if (showDropdown && searchResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
          return;
        }
        if (e.key === 'Enter' && selectedIndex >= 0 && searchResults[selectedIndex]) {
          e.preventDefault();
          selectProduct(searchResults[selectedIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowDropdown(false);
          setSelectedIndex(-1);
          return;
        }
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const value = (e.target as HTMLInputElement).value;
        handleSubmit(value);
        // Clear input after scan
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setShowDropdown(false);
        setSearchResults([]);
      }
    },
    [handleSubmit, showDropdown, searchResults, selectedIndex, selectProduct],
  );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSelectedIndex(-1);
  }, []);

  return {
    inputRef,
    handleKeyDown,
    handleInputChange,
    focusInput,
    searchResults,
    isSearching,
    showDropdown,
    selectedIndex,
    selectProduct,
    closeDropdown,
  };
}
